import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { LockKeyholeOpen } from "lucide-react";
import { cn } from "@/utils/cn";

type ConfidentialModeControlProps = {
  disabled?: boolean;
  isStarting?: boolean;
  onStart: () => Promise<void>;
};

export function ConfidentialModeControl({
  disabled = false,
  isStarting = false,
  onStart,
}: ConfidentialModeControlProps) {
  const { t } = useTranslation();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const isDisabled = disabled || isStarting;

  return (
    <Popover
      open={confirmationOpen && !isDisabled}
      onOpenChange={(open) => {
        if (!isDisabled) setConfirmationOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isDisabled}
          aria-label={t("playground.confidentialMode")}
          className={cn(
            "group relative flex h-10 w-10 items-center justify-start overflow-hidden rounded-lg border border-[var(--confidential-control-border)] bg-[var(--confidential-control-background)] px-4 py-[7.5px] text-sm font-medium leading-[1.5] tracking-[0.07px] text-foreground shadow-sm transition-[width,background-color] duration-200 motion-reduce:transition-none",
            isDisabled
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer hover:w-[177px] focus-visible:w-[177px] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
            confirmationOpen && "w-[177px]",
          )}
        >
          <LockKeyholeOpen
            className={cn(
              "absolute start-1/2 size-4 shrink-0 -translate-x-1/2 transition-[inset-inline-start,transform] duration-200 motion-reduce:transition-none rtl:translate-x-1/2",
              !isDisabled &&
                "group-hover:start-4 group-hover:translate-x-0 group-focus-visible:start-4 group-focus-visible:translate-x-0 rtl:group-hover:translate-x-0 rtl:group-focus-visible:translate-x-0",
              confirmationOpen &&
                "start-4 translate-x-0 rtl:translate-x-0",
            )}
          />
          <span
            className={cn(
              "ms-6 max-w-0 shrink-0 whitespace-nowrap opacity-0 transition-[max-width,opacity] duration-200 motion-reduce:transition-none",
              !isDisabled &&
                "group-hover:max-w-36 group-hover:opacity-100 group-focus-visible:max-w-36 group-focus-visible:opacity-100",
              confirmationOpen && "max-w-36 opacity-100",
            )}
          >
            {t("playground.confidentialMode")}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        collisionPadding={12}
        className="relative w-[min(343px,calc(100vw-24px))] overflow-visible rounded-lg border-border bg-card p-0 text-card-foreground shadow-sm"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex h-[72px] flex-col justify-center p-3">
          <h2 className="text-sm font-normal leading-[1.5] tracking-[0.07px] text-foreground">
            {t("playground.startConfidentialSession")}
          </h2>
          <p className="text-xs font-normal leading-[1.5] tracking-[0.18px] text-muted-foreground">
            {t("playground.confidentialConfirmationDescription")}
          </p>
        </div>
        <div className="grid min-h-[60px] grid-cols-2 gap-2 p-3">
          <Button
            type="button"
            variant="outline"
            disabled={isStarting}
            onClick={() => setConfirmationOpen(false)}
            className="h-9 rounded-lg border-muted-foreground bg-accent/50 px-4 py-[7.5px] text-sm font-medium leading-[1.5] tracking-[0.07px] shadow-sm"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            disabled={isStarting}
            className="h-9 rounded-lg px-4 py-[7.5px] text-sm font-medium leading-[1.5] tracking-[0.07px]"
            onClick={() => {
              void onStart()
                .then(() => setConfirmationOpen(false))
                .catch(() => undefined);
            }}
          >
            {isStarting
              ? t("playground.startingSession")
              : t("playground.startSession")}
          </Button>
        </div>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-[5px] end-[83px] size-2.5 rotate-45 border-l border-t border-border bg-card"
        />
      </PopoverContent>
    </Popover>
  );
}
